// ============================================================================
// HandHistoryDrawer - CoinPoker-style hand history for the current table
// ============================================================================
// Reads tables/{tableId}/handHistory (written server-side on every completed
// hand). Shows the last 21 hands with prev/next navigation, a Summary view
// (positions, revealed cards, net results) and a Details view (street-by-
// street action log). Hole cards appear only if they were publicly revealed
// at showdown - folded hands render card backs, exactly like the reference.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerBody, DrawerHeader,
  Box, Text, HStack, VStack, Badge, Spinner, IconButton, Divider,
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { collection, onSnapshot, orderBy, query, limitToLast } from 'firebase/firestore';
import { db } from '../firebase';
import gameApiService from '../services/gameApiService';
import { POKER_COLORS } from '../theme/designSystem';

const SHOW_HANDS = 21;

interface HandHistoryDrawerProps {
  tableId: string;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
  /** uid -> display name, the same map the felt uses. Recorded hands carry
   *  names, but the LIVE gameState often has name=null - without this the
   *  live entry showed raw uid prefixes. */
  usernames?: { [uid: string]: string };
  /** The CURRENT table doc's createdAt. Featured-table resets recreate the
   *  table under the same id and Firestore keeps the old doc's
   *  subcollections, so history can contain GHOST hands from a previous
   *  incarnation - strangers' hands with clashing hand numbers. Anything
   *  recorded before this table existed is not this table's history. */
  tableCreatedAt?: any;
  // Live table gameState - when a hand is in progress it appears as the
  // newest entry ("LIVE") so you can review the preflop raise while the
  // river is still being bet, CoinPoker-style. The server appends every
  // action to gameState.actionLog as it happens.
  liveGameState?: any;
}

const suitGlyph = (s?: string) =>
  s === 'hearts' ? '♥' : s === 'diamonds' ? '♦' : s === 'clubs' ? '♣' : s === 'spades' ? '♠' : '';
const suitColor = (s?: string) => (s === 'hearts' || s === 'diamonds' ? '#e53e3e' : '#1a202c');

const MiniCard: React.FC<{ card?: { rank: string; suit: string } | null; back?: boolean }> = ({ card, back }) => {
  if (back || !card?.rank) {
    return (
      <Box w="30px" h="42px" borderRadius="4px"
        bg="repeating-linear-gradient(45deg, #b41c30 0 4px, #8f1424 4px 8px)"
        border="1px solid #7a0c1c" />
    );
  }
  return (
    <Box w="30px" h="42px" borderRadius="4px" bg="white" border="1px solid #cbd5e0"
      display="flex" flexDirection="column" alignItems="center" justifyContent="center"
      lineHeight="1">
      <Text fontSize="13px" fontWeight="800" color={suitColor(card.suit)}>{card.rank}</Text>
      <Text fontSize="12px" color={suitColor(card.suit)}>{suitGlyph(card.suit)}</Text>
    </Box>
  );
};

const actionColors: Record<string, string> = {
  'fold': 'gray', 'check': 'teal', 'call': 'green', 'raise': 'orange', 'all-in': 'red',
};

const HandHistoryDrawer: React.FC<HandHistoryDrawerProps> = ({
  tableId, isOpen, onClose, currentUserId, liveGameState, usernames = {}, tableCreatedAt,
}) => {
  const [hands, setHands] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // null = follow the newest hand (including the live one) as it updates;
  // a number = the user navigated backwards and is pinned to that entry.
  const [cursor, setCursor] = useState<number | null>(null);
  const [tab, setTab] = useState<'summary' | 'details'>('summary');

  useEffect(() => {
    if (!isOpen || !tableId) return;
    // Ordered by createdAt, NOT handNumber. Featured-table resets recreate
    // the table under the same id, and Firestore keeps the old doc's
    // subcollections - so the fresh table starts at hand 1 while stale docs
    // sit at 100+. Ordered by number, limitToLast returned only the stale
    // block and a hand played seconds ago never appeared. Time ordering
    // makes the newest hand the newest thing, whatever its number.
    const q = query(
      collection(db, 'tables', tableId, 'handHistory'),
      orderBy('createdAt'),
      limitToLast(SHOW_HANDS)
    );
    // Ghost filter: drop hands recorded before this table incarnation was
    // created (60s slack for clock skew). Without it, "back one" from the
    // live hand jumped to a stranger's Hand #21 from the pre-reset table.
    const epochMs = (() => {
      const t: any = tableCreatedAt;
      if (!t) return 0;
      if (typeof t?.toMillis === 'function') return t.toMillis() - 60_000;
      const parsed = new Date(t as any).getTime();
      return isNaN(parsed) ? 0 : parsed - 60_000;
    })();
    const unsub = onSnapshot(q, snap => {
      const rows = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as any))
        .filter(h => {
          if (!epochMs) return true;
          const at = h.createdAt?.toMillis?.() ?? (h.createdAt ? new Date(h.createdAt).getTime() : 0);
          return at >= epochMs;
        });
      setHands(rows);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [isOpen, tableId, tableCreatedAt]);

  // Your OWN dealt cards, fetched from the server per hand. They used to
  // ride inside the shared history docs as a map of EVERY player's cards -
  // the UI only rendered yours, but the data itself was readable by anyone,
  // which for a poker app is an information leak. Now the server returns
  // only the caller's cards and nothing else exists client-side.
  const [ownCards, setOwnCards] = useState<Record<number, any[]>>({});
  const ownCardsFetchedRef = React.useRef<string>('');
  useEffect(() => {
    if (!isOpen || !tableId || !currentUserId || hands.length === 0) return;
    const nums = hands.map(h => h.handNumber).filter(n => Number.isInteger(n));
    const key = `${tableId}:${nums.join(',')}`;
    if (key === ownCardsFetchedRef.current) return;
    ownCardsFetchedRef.current = key;
    gameApiService.handOwnCards(tableId, nums).then(r => {
      if (r.success && r.cards) setOwnCards(r.cards);
    });
  }, [isOpen, tableId, currentUserId, hands]);

  // Synthesize a history entry for the hand in progress from the live
  // gameState. Hole cards stay hidden (card backs) unless the server has
  // revealed them (all-in runout / showdown) - same rule as the felt.
  const liveHand = useMemo(() => {
    const gs = liveGameState;
    if (!gs || gs.status !== 'playing') return null;
    if (!gs.round || gs.round === 'ended' || gs.round === 'waiting') return null;
    const log: any[] = Array.isArray(gs.actionLog) ? gs.actionLog : [];
    return {
      id: 'live',
      live: true,
      handNumber: gs.handNumber,
      blinds: { sb: gs.smallBlind, bb: gs.bigBlind },
      communityCards: gs.communityCards || [],
      totalPot: gs.pot || 0,
      actionLog: log,
      players: (gs.players || []).filter((p: any) => p?.id).map((p: any) => ({
        id: p.id,
        name: p.name,
        position: p.position || '',
        isBot: typeof p.id === 'string' && p.id.startsWith('house_bot_'),
        lastAction: p.folded ? 'fold' : p.lastAction,
        holeCards: p.holeCards?.length === 2 && p.holeCards[0]?.rank ? p.holeCards : null,
        // "Net" during a live hand = what they've committed so far.
        net: -(p.totalBetThisHand || p.currentBet || 0),
      })),
      winners: [],
    };
  }, [liveGameState]);

  const allHands = useMemo(
    () => (liveHand ? [...hands, liveHand] : hands),
    [hands, liveHand]
  );

  const effCursor = cursor === null
    ? allHands.length - 1
    : Math.min(cursor, allHands.length - 1);
  const hand = effCursor >= 0 ? allHands[effCursor] : null;

  const streets = useMemo(() => {
    if (!hand?.actionLog) return [];
    const order = ['preflop', 'flop', 'turn', 'river'];
    const grouped: { street: string; actions: any[] }[] = [];
    for (const street of order) {
      const actions = (hand.actionLog as any[]).filter(a => a?.r === street);
      if (actions.length > 0) grouped.push({ street, actions });
    }
    return grouped;
  }, [hand]);

  const nameOf = (uid: string) => {
    const p = (hand?.players || []).find((x: any) => x.id === uid);
    return usernames[uid] || p?.name || uid.substring(0, 8);
  };
  const positionOf = (uid: string) => {
    const p = (hand?.players || []).find((x: any) => x.id === uid);
    return p?.position || '';
  };

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg="gray.900" color="white">
        <DrawerCloseButton color="gray.400" />
        <DrawerHeader borderBottomWidth="1px" borderColor="gray.700" pb={3}>
          <HStack>
            <ScrollText size={18} color={POKER_COLORS.cyan} />
            <Text fontSize="md" fontWeight="bold">Hand History</Text>
          </HStack>
          {hand && (
            <HStack mt={1} fontSize="xs" color="gray.400" justify="space-between">
              <Text>NLH - {hand.blinds?.sb}/{hand.blinds?.bb}</Text>
              <HStack spacing={2}>
                {hand.live && (
                  <Badge colorScheme="red" fontSize="9px" variant="solid">LIVE</Badge>
                )}
                <Text>Hand #{hand.handNumber}</Text>
              </HStack>
            </HStack>
          )}
        </DrawerHeader>

        <DrawerBody px={3} py={3}>
          {loading ? (
            <Box textAlign="center" py={10}><Spinner color="cyan.400" /></Box>
          ) : allHands.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={10} fontSize="sm">
              No hands yet. The current hand appears here as soon as the first
              action is taken; completed hands stay for review.
            </Text>
          ) : hand ? (
            <VStack align="stretch" spacing={3}>
              {/* Community cards + pot */}
              <Box bg="gray.800" borderRadius="lg" p={3}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.400">Community Cards</Text>
                  <HStack spacing={1}>
                    {(hand.communityCards || []).map((c: any, i: number) => (
                      <MiniCard key={i} card={c} />
                    ))}
                    {Array.from({ length: Math.max(0, 5 - (hand.communityCards || []).length) }).map((_, i) => (
                      <Box key={`e-${i}`} w="30px" h="42px" borderRadius="4px"
                        border="1px dashed rgba(255,255,255,0.2)" />
                    ))}
                  </HStack>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.400">Total Pot</Text>
                  <Text fontSize="md" fontWeight="bold">{(hand.totalPot || 0).toLocaleString()}</Text>
                </HStack>
                {hand.foldWin && (
                  <Badge mt={1} colorScheme="teal" fontSize="9px">WON WITHOUT SHOWDOWN</Badge>
                )}
              </Box>

              {/* Tab switch */}
              <HStack spacing={0} borderBottom="1px solid" borderColor="gray.700">
                {(['summary', 'details'] as const).map(t => (
                  <Box key={t} flex={1} textAlign="center" py={2} cursor="pointer"
                    onClick={() => setTab(t)}
                    borderBottom="2px solid"
                    borderColor={tab === t ? 'red.400' : 'transparent'}
                    color={tab === t ? 'white' : 'gray.500'}
                    fontWeight={tab === t ? 'bold' : 'normal'}
                    fontSize="sm" textTransform="capitalize">
                    {t}
                  </Box>
                ))}
              </HStack>

              {tab === 'summary' ? (
                <VStack align="stretch" spacing={2}>
                  {(hand.players || []).map((p: any) => {
                    const isMe = p.id === currentUserId;
                    // Your OWN dealt cards even when you folded/mucked -
                    // sit-out players can review exactly what they missed.
                    // Never rendered for anyone else's seat.
                    const mine = ownCards[hand.handNumber];
                    const myFoldedCards = isMe && !p.holeCards
                      && Array.isArray(mine) && mine.length === 2
                      ? mine : null;
                    return (
                      <Box key={p.id} bg="gray.800" borderRadius="md" p={3}
                        border={isMe ? '1px solid rgba(6,182,212,0.5)' : undefined}>
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing={1}>
                            <HStack>
                              {p.position && <Badge fontSize="9px" colorScheme="gray">{p.position}</Badge>}
                              <Text fontSize="sm" fontWeight={isMe ? 'bold' : 'semibold'}
                                color={isMe ? 'cyan.300' : 'white'}>
                                {usernames[p.id] || p.name || p.id.substring(0, 8)}
                              </Text>
                              {p.isBot && <Badge fontSize="8px" colorScheme="purple">BOT</Badge>}
                            </HStack>
                            <HStack spacing={1}>
                              {p.holeCards ? (
                                <>
                                  <MiniCard card={p.holeCards[0]} />
                                  <MiniCard card={p.holeCards[1]} />
                                </>
                              ) : myFoldedCards ? (
                                <Box opacity={0.65} display="flex" gap="4px" title="Your cards (folded)">
                                  <MiniCard card={myFoldedCards[0]} />
                                  <MiniCard card={myFoldedCards[1]} />
                                </Box>
                              ) : (
                                <>
                                  <MiniCard back />
                                  <MiniCard back />
                                </>
                              )}
                            </HStack>
                          </VStack>
                          <VStack align="end" spacing={1}>
                            {p.lastAction && (
                              <Badge colorScheme={actionColors[p.lastAction] || 'gray'} fontSize="10px">
                                {String(p.lastAction).toUpperCase()}
                              </Badge>
                            )}
                            <Text fontSize="sm" fontWeight="bold"
                              color={p.net > 0 ? 'green.300' : p.net < 0 ? 'red.300' : 'gray.400'}>
                              {p.net > 0 ? '+' : ''}{(p.net || 0).toLocaleString()}
                            </Text>
                            {(hand.winners || []).find((w: any) => w.playerId === p.id)?.hand && (
                              <Text fontSize="10px" color="green.200">
                                {(hand.winners || []).find((w: any) => w.playerId === p.id).hand}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              ) : (
                <VStack align="stretch" spacing={3}>
                  {streets.length === 0 && (
                    <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
                      No action log for this hand.
                    </Text>
                  )}
                  {streets.map(({ street, actions }) => (
                    <Box key={street}>
                      <Text fontSize="xs" color="gray.500" fontWeight="bold"
                        letterSpacing="wide" textTransform="uppercase" mb={1}>
                        {street}
                      </Text>
                      <VStack align="stretch" spacing={1}>
                        {actions.map((a: any, i: number) => (
                          <HStack key={i} bg="gray.800" borderRadius="sm" px={2} py={1.5}
                            justify="space-between">
                            <HStack>
                              {positionOf(a.p) && <Badge fontSize="8px" colorScheme="gray">{positionOf(a.p)}</Badge>}
                              <Text fontSize="xs" color={a.p === currentUserId ? 'cyan.300' : 'gray.200'}>
                                {nameOf(a.p)}
                              </Text>
                            </HStack>
                            <HStack>
                              <Badge colorScheme={actionColors[a.a] || 'gray'} fontSize="9px">
                                {String(a.a).toUpperCase()}
                              </Badge>
                              {a.amt > 0 && (
                                <Text fontSize="xs" color="gray.300">{a.amt.toLocaleString()}</Text>
                              )}
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              )}

              <Divider borderColor="gray.700" />

              {/* Prev / next navigation */}
              <HStack justify="center" spacing={6} pb={2}>
                <IconButton aria-label="Previous hand" icon={<ChevronLeft size={18} />}
                  size="sm" variant="outline" colorScheme="gray" isRound
                  isDisabled={effCursor <= 0}
                  onClick={() => setCursor(Math.max(0, effCursor - 1))} />
                <VStack spacing={0}>
                  <Text fontSize="md" fontWeight="bold">
                    {effCursor + 1} / {allHands.length}
                  </Text>
                  <Text fontSize="10px" color="gray.500">
                    {hand.live ? 'Live hand' : `Showing last ${allHands.length} hands`}
                  </Text>
                </VStack>
                <IconButton aria-label="Next hand" icon={<ChevronRight size={18} />}
                  size="sm" variant="outline" colorScheme="gray" isRound
                  isDisabled={effCursor >= allHands.length - 1}
                  onClick={() => {
                    // Stepping onto the newest entry resumes "follow" mode so
                    // the drawer keeps tracking the live hand as it updates.
                    const next = effCursor + 1;
                    setCursor(next >= allHands.length - 1 ? null : next);
                  }} />
              </HStack>
            </VStack>
          ) : null}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default HandHistoryDrawer;
